import React, { InputHTMLAttributes, forwardRef } from "react";
import { Eye, EyeOff } from "lucide-react";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string | undefined;
  helperText?: string;
  showPasswordToggle?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      showPasswordToggle = false,
      type,
      className = "",
      ...props
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const [inputType, setInputType] = React.useState(type);

    React.useEffect(() => {
      if (showPasswordToggle && type === "password") {
        setInputType(showPassword ? "text" : "password");
      } else {
        setInputType(type);
      }
    }, [showPassword, type, showPasswordToggle]);

    const baseClasses =
      "appearance-none block w-full min-h-[44px] sf-input";
    const errorClasses = error
      ? "!border-neon-pink/60"
      : "";

    return (
      <div className="space-y-1.5">
        {label && (
          <label className="block text-sm font-semibold text-fg-soft">
            {label}
          </label>
        )}

        <div className="relative">
          <input
            ref={ref}
            type={inputType}
            className={`${baseClasses} ${errorClasses} ${showPasswordToggle ? "pr-10" : ""} ${className}`}
            {...props}
          />

          {showPasswordToggle && type === "password" && (
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-2 flex items-center min-w-[44px] justify-center"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5 text-fg-faint" />
              ) : (
                <Eye className="h-5 w-5 text-fg-faint" />
              )}
            </button>
          )}
        </div>

        {error && <p className="text-sm text-neon-pink-soft">{error}</p>}

        {helperText && !error && (
          <p className="text-sm text-fg-muted">{helperText}</p>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";

export default Input;
